import { useFieldArray, useFormContext, type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CardImobiliariaCorretor } from '@/features/proposta/components/card-imobiliaria-corretor'
import type { CadastroImobiliariaCorretorEntity } from '@/features/proposta/entities/cadastro-imobiliaria-corretor.entity'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import { Button } from '@/shared/components/ui'
import { SelectField } from '@/shared/components/ui/select'

type TCadastroImobiliariaCorretorFormProps = {
  cadastroImobiliariaCorretoresEntity: CadastroImobiliariaCorretorEntity
  exibeFormaPagamentoComissao?: boolean
  control: Control<TPropostaSchemaInput>
  formaPagamentoComissao?: 'PagaPelaPlaenge' | 'PagaPeloCliente'
  isAdicionarNovaParticipacaoDesabilitado: boolean
}

export const CadastroImobiliariaCorretorForm = ({
  control,
  formaPagamentoComissao,
  cadastroImobiliariaCorretoresEntity,
  exibeFormaPagamentoComissao,
  isAdicionarNovaParticipacaoDesabilitado,
}: TCadastroImobiliariaCorretorFormProps) => {
  const { t } = useTranslation('proposta')
  const { trigger } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'detalhesDaProposta.imobiliariasCorretores.participacoes',
  })

  const isExibirCamposPreenchimento =
    !exibeFormaPagamentoComissao || (exibeFormaPagamentoComissao && formaPagamentoComissao)

  return (
    <div className="border-muted flex w-full flex-col items-start border-1 p-4">
      <Render.If condition={exibeFormaPagamentoComissao}>
        {() => (
          <SelectField
            control={control}
            className="w-full lg:max-w-65"
            name="detalhesDaProposta.imobiliariasCorretores.formaPagamentoComissao"
            label={t('detalhes_proposta.imobiliaria.inputs.forma_comissao.label')}
            options={[
              { value: 'PagaPelaPlaenge', label: t('detalhes_proposta.imobiliaria.inputs.forma_comissao.plaenge') },
              { value: 'PagaPeloCliente', label: t('detalhes_proposta.imobiliaria.inputs.forma_comissao.cliente') },
            ]}
            labelKey="label"
            valueKey="value"
          />
        )}
      </Render.If>
      <Render.If condition={isExibirCamposPreenchimento}>
        {() => (
          <div className="mt-4 flex flex-col items-start gap-4 md:gap-1">
            {fields.map((participacao, index) => (
              <CardImobiliariaCorretor
                key={participacao.id}
                index={index}
                onExcluirParticipacao={() => {
                  remove(index)
                  trigger('detalhesDaProposta.imobiliariasCorretores.participacoes')
                }}
              />
            ))}
            <Button
              variant="link"
              className="text-wrap whitespace-normal aria-disabled:cursor-not-allowed aria-disabled:border-neutral-500 aria-disabled:text-neutral-500"
              onClick={() => append(cadastroImobiliariaCorretoresEntity.novoCadastro)}
              aria-disabled={isAdicionarNovaParticipacaoDesabilitado}
              disabled={isAdicionarNovaParticipacaoDesabilitado}
            >
              <Icon name="plus" />
              {t('detalhes_proposta.imobiliaria.botoes.adicionar')}
            </Button>
          </div>
        )}
      </Render.If>
    </div>
  )
}
